import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { SortSelect } from '@/shared/components/sort-select';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import { useSelection } from '@/shared/lib/use-selection';
import { PageHeader } from '@/shared/components/page-header';
import { FloatingActionBar } from '@/shared/components/floating-action-bar';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { useConfirm } from '@/shared/components/confirm';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import { Pagination } from '@/shared/ui/pagination';
import type { Campaign } from '@/shared/types/campaign';
import { fetchCampaigns, removeCampaign } from '../api/use-campaigns';
import { CampaignCard } from './campaign-card';
import { CampaignCreateDialog } from './campaign-create-dialog';

const PAGE_SIZE = 25;

export function ManualCampaignsPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const isOperatorMulti = user?.role === 'Operator' && Boolean(passbook.hasMultiStore);
  const multiUserId = isOperatorMulti ? user?._id : undefined;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [sortValue, setSortValue] = useState('date:desc');
  const [batchBusy, setBatchBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data = [], isLoading } = useQuery({
    queryKey: ['campaigns', 'manual', idPassbook, multiUserId ?? 'all'],
    queryFn: () =>
      fetchCampaigns(idPassbook, 'manual', multiUserId ? { userId: multiUserId } : undefined) as Promise<
        Campaign[]
      >,
    staleTime: 60_000,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const result = q
      ? data.filter((c) => {
          const msg = (c.description ?? c.message ?? '').toLowerCase();
          return (c.name ?? '').toLowerCase().includes(q) || msg.includes(q);
        })
      : [...data];

    if (sortValue === 'name:asc') result.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    else if (sortValue === 'name:desc') result.sort((a, b) => (b.name ?? '').localeCompare(a.name ?? ''));
    else if (sortValue === 'date:asc') result.sort((a, b) => (a.updated ?? '').localeCompare(b.updated ?? ''));
    else result.sort((a, b) => (b.updated ?? '').localeCompare(a.updated ?? ''));

    return result;
  }, [data, debouncedSearch, sortValue]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, sortValue]);

  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, pageIndex]);

  const selection = useSelection(pageRows);

  useEffect(() => {
    selection.clear();
  }, [pageIndex, debouncedSearch]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['campaigns', 'manual', idPassbook] });
    selection.clear();
  }, [queryClient, idPassbook, selection]);

  const removeOne = useCallback(
    async (id: string) => {
      const proceed = await confirm({
        title: 'Remover mensagem',
        description: 'Tem certeza que deseja remover esta mensagem? Esta ação não pode ser desfeita.',
        confirmLabel: 'Remover',
        variant: 'destructive',
      });
      if (!proceed) return;
      try {
        await removeCampaign(idPassbook, id);
        toast.success('Mensagem removida.');
        refresh();
      } catch {
        toast.error('Falha ao remover.');
      }
    },
    [idPassbook, refresh, confirm],
  );

  const batchRemove = useCallback(async () => {
    if (selection.selected.length === 0) return;
    const proceed = await confirm({
      title: 'Remover mensagens',
      description: `Remover ${selection.selected.length} mensagem(ns) selecionada(s)?`,
      confirmLabel: 'Remover',
      variant: 'destructive',
    });
    if (!proceed) return;
    setBatchBusy(true);
    let ok = 0;
    for (const c of selection.selected) {
      try {
        await removeCampaign(idPassbook, c._id);
        ok += 1;
      } catch {
        /* continua */
      }
    }
    setBatchBusy(false);
    if (ok > 0) {
      toast.success(`${ok} mensagem(ns) removida(s).`);
      refresh();
    } else {
      toast.error('Falha ao remover mensagens.');
    }
  }, [selection, idPassbook, refresh, confirm]);

  return (
    <>
      <PageHeader
        title="Mensagens no cartão"
        hideMetaOnMobile
        description={`${passbook.organizationName} · aviso único para todos os cartões, sem segmentação nem agendamento`}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Nova mensagem
          </Button>
        }
      />

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome ou mensagem…"
        toolbarExtra={
          <SortSelect
            value={sortValue}
            onChange={setSortValue}
            options={[
              { value: 'date:desc', label: 'Mais recentes' },
              { value: 'date:asc', label: 'Mais antigas' },
              { value: 'name:asc', label: 'Nome A→Z' },
              { value: 'name:desc', label: 'Nome Z→A' },
            ]}
          />
        }
        selectAllId="select-all-campaigns-manual"
        itemCount={pageRows.length}
        allOnPageSelected={selection.allOnPageSelected}
        someOnPageSelected={selection.someOnPageSelected}
        onToggleAllOnPage={selection.toggleAllOnPage}
        isLoading={isLoading}
        isEmpty={!isLoading && filtered.length === 0}
        emptyTitle="Nenhuma mensagem"
        emptyDescription="As mensagens enviadas para os cartões aparecem aqui."
        footer={
          filtered.length > PAGE_SIZE ? (
            <Pagination
              page={pageIndex}
              pageSize={PAGE_SIZE}
              total={filtered.length}
              onPageChange={setPageIndex}
              className="justify-center pt-2"
            />
          ) : null
        }
      >
        {pageRows.map((campaign, index) => (
          <li key={campaign._id}>
            <CampaignCard
              campaign={campaign}
              mode="manual"
              index={index}
              selected={selection.selectedIds.has(campaign._id)}
              onToggle={() => selection.toggle(campaign._id)}
              onRemove={() => removeOne(campaign._id)}
            />
          </li>
        ))}
      </SelectableListLayout>

      <FloatingActionBar count={selection.count} onClear={selection.clear}>
        <Tooltip content="Excluir mensagens selecionadas" side="top">
          <Button size="sm" variant="destructive" disabled={batchBusy} onClick={batchRemove}>
            <Trash2 className="size-4" /> Remover
          </Button>
        </Tooltip>
      </FloatingActionBar>

      <CampaignCreateDialog
        idPassbook={idPassbook}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={refresh}
        multiStore={isOperatorMulti}
        userId={multiUserId}
      />
    </>
  );
}
